import React from 'react'
import DiscountSummaryCard from './summary/DiscountSummaryCard'
import CustomerDiscountEligibilityV2 from './setup/CustomerDiscountEligibilityV2'
import { DiscountFormProvider } from './hooks/useDiscountForm'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import DiscountUsageLimitsV2 from './setup/UsageLimits/DiscountUsageLimitsV2'
import AvailabilityValidityV2 from './setup/Availability/AvailabilityValidityV2'
import DiscountMethodV2 from './setup/Method/DiscountMethodV2'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import useSWR from 'swr'
import { fetcher, staticOptions } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import {
  parseDiscountResponseV2,
  getDiscountApplicationRules,
  enrichExcludedCataloguePlaceholders
} from 'src/lib/utils/discounts/discountsV2Helpers'
import { useDiscountForm } from './hooks/useDiscountForm'
import { DiscountResponseV2, DiscountBackendDataV2, UiConfigDiscountType } from 'src/lib/types/discountsV2'
import DiscountV2Layout from './layout/DiscountV2Layout'
import DiscountFormSkeleton from './common/DiscountFormSkeleton'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import DiscountApplicationRulesV2 from './setup/DiscountApplicationRules/DiscountApplicationRulesV2'
import { useUser } from 'src/lib/contexts/UserContext'
import { CopiedDiscountInfo } from 'src/lib/types/common/discounts'
import { SectionInteractionProvider } from 'src/components/common/context/SectionInteractionContext'
import { SectionWrapper } from 'src/components/common/context/SectionWrapper'
import DiscountCombinationModeV2 from './setup/DiscountCombination/DiscountCombinationModeV2'
import { errorToast } from 'src/lib/utils/toasters'

const ISSUER_ID = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

const DiscountCreationFlowV2 = ({
  couponId,
  ignoreLayout = false,
  discountType
}: {
  couponId?: string
  ignoreLayout?: boolean
  discountType?: UiConfigDiscountType
}) => {
  const router = useRouter()
  const { discountId } = router.query

  return (
    <DiscountFormProvider>
      <DiscountFormContent
        discountId={couponId || (discountId as string)}
        ignoreLayout={ignoreLayout}
        discountType={discountType}
      />
    </DiscountFormProvider>
  )
}

export const DiscountFormContent = ({
  discountId,
  ignoreLayout,
  copiedDiscountInfo,
  discountType
}: {
  discountId?: string
  ignoreLayout?: boolean
  copiedDiscountInfo?: CopiedDiscountInfo
  discountType?: UiConfigDiscountType
}) => {
  const hideLegacyBuilder = discountType === UiConfigDiscountType.EXIT_COUPON
  const {
    getters: { merchant }
  } = useMerchant()
  const {
    state,
    actions: { setFormState, markSaved }
  } = useDiscountForm()

  const isExitCoupon = state.uiConfig.type === UiConfigDiscountType.EXIT_COUPON

  const { user, canAccess } = useUser()
  const { setLoaderProperties } = useLoader()

  const router = useRouter()
  const { type } = router.query

  const {
    data: discountData,
    error: discountError,
    isLoading: isDiscountLoading,
    isValidating: isDiscountValidating
  } = useSWR<DiscountBackendDataV2>(
    () => {
      return discountId && merchant?.accountId
        ? `/issuer/${ISSUER_ID}/merchant/${merchant.accountId}/coupon/${discountId}`
        : null
    },
    async (url: string) => {
      const res = (await fetcher(url, apiURI.DISCOUNT_ENGINE)) as DiscountResponseV2
      return res?.data
    },
    // Retry on failure, else one timeout leaves the form on its defaults. Opening an edit form
    // must always refetch, so disable deduping — otherwise a quick reopen is served from cache with
    // no revalidation at all, and the form would sit on the skeleton waiting for one.
    { ...staticOptions, shouldRetryOnError: true, dedupingInterval: 0 }
  )

  const hydratedDataRef = useRef<DiscountBackendDataV2 | null>(null)
  const loadErrorToastedRef = useRef<string | null>(null)
  const [isDiscountHydrated, setIsDiscountHydrated] = useState(false)

  // Only a cached copy can be stale. On a fresh page load the cache is empty, so there is nothing
  // to guard against — and guarding anyway is what left reloads sitting on the skeleton forever.
  const cacheAtMountRef = useRef<{ captured: boolean; data?: DiscountBackendDataV2 }>({ captured: false })
  if (!cacheAtMountRef.current.captured) {
    cacheAtMountRef.current = { captured: true, data: discountData }
  }
  const startedFromCache = cacheAtMountRef.current.data !== undefined

  const sawValidationRef = useRef(false)
  const [hasRevalidated, setHasRevalidated] = useState(false)

  useEffect(() => {
    if (isDiscountValidating) {
      sawValidationRef.current = true
      return
    }
    if (sawValidationRef.current) {
      setHasRevalidated(true)
    }
  }, [isDiscountValidating])

  // A payload that differs from the one we mounted with is proof of freshness on its own, so we are
  // never solely dependent on catching SWR's transient `isValidating` phase.
  const hasNewerData = discountData !== undefined && discountData !== cacheAtMountRef.current.data
  const isAwaitingFreshDiscount = startedFromCache && !hasNewerData && !hasRevalidated

  // Hydrate from `data`, not SWR's `onSuccess` — `onSuccess` only fires for the hook that started the
  // request, and this tree can remount mid-request (CopilotShell, once the user profile lands).
  // `user` is required because the parser reads permissions.
  useEffect(() => {
    if (!discountId || !discountData || !user) return

    // Never seed the form from a cached copy that might predate the last save.
    if (isAwaitingFreshDiscount) return

    // Track the payload we hydrated from, not the discount id. SWR is cache-first, so re-opening a
    // discount renders its cached copy before the revalidated one — keying on the id alone committed
    // that stale copy and then ignored the fresh response, which is what made a just-saved change
    // look like it hadn't stuck until a full page reload. SWR hands back the same object when a
    // revalidation comes back unchanged, so this re-runs only when the discount really changed.
    if (hydratedDataRef.current === discountData) return
    hydratedDataRef.current = discountData

    let parsedState = parseDiscountResponseV2(discountData, { userData: user, canAccess })

    if (copiedDiscountInfo) {
      parsedState = {
        ...parsedState,
        entitlement: {
          ...parsedState.entitlement,
          applicationMethod: {
            ...parsedState.entitlement.applicationMethod,
            discountCode: '',
            title: ''
          }
        }
      }
    }

    setFormState(parsedState)
    markSaved({ ...state, ...parsedState })
    setIsDiscountHydrated(true)

    // Fetch product/collection/variant details for any placeholder chips the parser synthesized
    // (exclusion entities whose display metadata wasn't stored). Runs async in the background so
    // the form renders immediately with id-labelled chips, then updates once the real data lands.
    let cancelled = false
    void enrichExcludedCataloguePlaceholders(parsedState, ISSUER_ID, merchant?.accountId).then(enriched => {
      if (cancelled || !enriched) return
      setFormState(enriched)
      markSaved({ ...state, ...enriched })
    })
    return () => {
      cancelled = true
    }
  }, [discountData, discountId, user, copiedDiscountInfo, isAwaitingFreshDiscount, merchant?.accountId])

  // A failed load otherwise looks identical to a new discount.
  useEffect(() => {
    if (!discountId || !discountError) return
    if (loadErrorToastedRef.current === discountId) return
    loadErrorToastedRef.current = discountId

    errorToast('Could not load this discount. Please refresh the page.')
  }, [discountError, discountId])

  useEffect(() => {
    if (discountId) {
      setLoaderProperties({ isLoaderVisible: isDiscountLoading })
    }
  }, [discountId, isDiscountLoading])

  useEffect(() => {
    if (!discountId) {
      const rulesState = getDiscountApplicationRules(
        (discountType ?? type ?? state.uiConfig.type) as UiConfigDiscountType
      )
      setFormState({
        ...rulesState,
        prerequisite: {
          ...state.prerequisite,
          ...rulesState.prerequisite
        },
        entitlement: {
          ...state.entitlement,
          ...rulesState.entitlement
        },
        configuration: {
          ...state.configuration,
          ...rulesState.configuration
        }
      })
    }
  }, [discountData])

  return (
    <DiscountV2Layout
      ignoreLayout={ignoreLayout}
      hideLegacyBuilder={hideLegacyBuilder}
      disableSave={Boolean(discountId) && !isDiscountHydrated}
    >
      {/* Until the revalidated discount lands there is nothing truthful to render — the cached copy
          is what put the pre-save config on screen — so hold the layout with a skeleton. */}
      {Boolean(discountId) && (!discountData || isAwaitingFreshDiscount) ? (
        <DiscountFormSkeleton />
      ) : (
        <SectionInteractionProvider>
          <div className='flex-1 flex w-full py-8 gap-16 px-12 items-start'>
            <div className='flex w-full justify-center '>
              <div className='flex flex-col xl:min-w-[640px] max-w-[640px]'>
                <div className='flex flex-col gap-8'>
                  <SectionWrapper sectionKey='discount-method'>
                    <DiscountMethodV2 />
                  </SectionWrapper>
                  <SectionWrapper sectionKey='discount-rules' overlayClassName='-inset-2'>
                    <DiscountApplicationRulesV2 />
                  </SectionWrapper>
                </div>

                <div className='h-px bg-zinc-200 w-full my-4'></div>

                <div className='flex flex-col'>
                  <SectionWrapper sectionKey='customer-eligibility'>
                    <CustomerDiscountEligibilityV2 />
                  </SectionWrapper>
                  <SectionWrapper sectionKey='usage-limits'>
                    <DiscountUsageLimitsV2 />
                  </SectionWrapper>

                  {!isExitCoupon && (
                    <SectionWrapper sectionKey='discount-combination'>
                      <DiscountCombinationModeV2 />
                    </SectionWrapper>
                  )}

                  <div className='h-px bg-zinc-200 w-full my-4'></div>

                  <AvailabilityValidityV2 />
                </div>
              </div>
            </div>

            <DiscountSummaryCard />
          </div>
        </SectionInteractionProvider>
      )}
    </DiscountV2Layout>
  )
}

export default DiscountCreationFlowV2
